export interface StoredOrder {
  orderId: string;
  orderNumber: string;
  orgUrl: string;
  tableId: string;
  estimatedTime: number;
  customerName: string;
  total: number;
  timestamp: number; // When the order was stored
  expiresAt: number; // When the order expires (12 hours from storage)
}

const ORDER_STORAGE_KEY = 'beanbuzz_table_orders';
// Max time to keep an order in localStorage, regardless of status
const ORDER_EXPIRATION_HOURS = 3;

export class OrderStorage {
  private static getStoredOrders(): StoredOrder[] {
    try {
      const stored = localStorage.getItem(ORDER_STORAGE_KEY);
      if (stored) {
        const orders: StoredOrder[] = JSON.parse(stored);
        // Filter out expired orders
        const now = Date.now();
        const validOrders = orders.filter(order => order.expiresAt > now);
        
        // Update localStorage with only valid orders
        if (validOrders.length !== orders.length) {
          localStorage.setItem(ORDER_STORAGE_KEY, JSON.stringify(validOrders));
        }
        
        return validOrders;
      }
      return [];
    } catch (error) {
      console.error('Error reading orders from localStorage:', error);
      return [];
    }
  }

  private static saveOrders(orders: StoredOrder[]): void {
    try {
      localStorage.setItem(ORDER_STORAGE_KEY, JSON.stringify(orders));
    } catch (error) {
      console.error('Error saving orders to localStorage:', error);
    }
  }

  static addOrder(orderData: {
    orderId: string;
    orderNumber: string;
    orgUrl: string;
    tableId: string;
    estimatedTime: number;
    customerName: string;
    total: number;
  }): void {
    const orders = this.getStoredOrders();
    const now = Date.now();
    const expiresAt = now + (ORDER_EXPIRATION_HOURS * 60 * 60 * 1000); // 3 hours

    const newOrder: StoredOrder = {
      ...orderData,
      timestamp: now,
      expiresAt
    };

    // Remove any existing order with the same orderId
    const filteredOrders = orders.filter(order => order.orderId !== orderData.orderId);
    
    // Add the new order
    filteredOrders.push(newOrder);
    
    this.saveOrders(filteredOrders);

    // Dispatch custom event to notify components
    window.dispatchEvent(new CustomEvent('orderStorageChanged', {
      detail: { action: 'add', order: newOrder }
    }));
  }

  static getOrders(): StoredOrder[] {
    return this.getStoredOrders();
  }

  static getOrdersForTable(orgUrl: string, tableId: string): StoredOrder[] {
    const orders = this.getStoredOrders();
    return orders.filter(order => 
      order.orgUrl === orgUrl && order.tableId === tableId
    );
  }

  static removeOrder(orderId: string): void {
    const orders = this.getStoredOrders();
    const filteredOrders = orders.filter(order => order.orderId !== orderId);
    this.saveOrders(filteredOrders);
  }

  static clearExpiredOrders(): void {
    this.getStoredOrders(); // This automatically cleans up expired orders
  }

  static clearAllOrders(): void {
    try {
      localStorage.removeItem(ORDER_STORAGE_KEY);
    } catch (error) {
      console.error('Error clearing orders from localStorage:', error);
    }
  }
} 